import { NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';

import { I18nService, TranslatePipe } from '@recruitee/i18n';

import { IconName } from '../../../icons-container/icons.type';
import { BadgeComponent } from '../../badge/badge.component';
import { CircularProgressIndicatorComponent } from '../../circular-progress-indicator/circular-progress-indicator.component';
import { DotComponent } from '../../dot/dot.component';
import { IconColorDirective } from '../../icon/directives/icon-color.directive';
import { IconComponent } from '../../icon/icon.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { RtTooltip } from '../../tooltip/tooltip.directive';

export type segmentedButtonType<T> = {
  isActive: boolean;
  isAvailable: boolean;
  isDisabled?: boolean;
  value: T;
  label?: string | undefined;
  counter?: number | undefined | null;
  iconName?: IconName | undefined;
  isUnread?: boolean;
};

export type LanguageSwitcherItem = {
  code: string;
  name: string;
  primary?: boolean;
  isDefault: boolean;
  translationFieldsCount: number;
};

@Component({
  selector: 'rt-segmented-control-button',
  templateUrl: './segmented-control-button.component.html',
  styleUrls: ['./segmented-control-button.component.less'],
  host: {
    '[class.active-btn]': 'isActive',
    '[class.disabled]': 'isDisabled',
    '[class.full-width]': 'fullwidth',
  },
  standalone: true,
  imports: [
    BadgeComponent,
    CircularProgressIndicatorComponent,
    IconColorDirective,
    IconComponent,
    NgIf,
    RtTooltip,
    SpinnerComponent,
    TranslatePipe,
    DotComponent,
  ],
})
export class SegmentedControlButtonComponent {
  @Input() public type: 'view-switcher' | 'multilang-switcher';
  @Input() public fullwidth: boolean;
  @Input() public isActive: boolean;
  @Input() public isAvailable: boolean;
  @Input() public label: string | undefined;
  @Input() public counter: number | undefined | null;
  @Input() public iconName: IconName | undefined;
  @Input() public isUnread?: boolean;
  @Input() public isDisabled: boolean | undefined;
  @Input() public language: LanguageSwitcherItem;
  @Input() public primaryLanguageLabel = 'app.common.default';
  @Input() public fieldsCount: number;
  @Input() public loadingLanguageCode: string | null = null;

  private bracketLabelTranslations = {
    default: this.i18n.translate('app.common.default'),
    primary: this.i18n.translate('app.languages.primary'),
  };

  constructor(private i18n: I18nService) {}

  public getTranslationProgress(translationFieldsCount: number): number {
    return this.fieldsCount ? (translationFieldsCount / this.fieldsCount) * 100 : 0;
  }

  public getTooltipMessage(langTermsCount: number): string {
    return `${langTermsCount} / ${this.fieldsCount} ${this.i18n.translate(
      'app.languages.fields_translated',
    )}`;
  }

  public get bracketLabel(): string {
    if (this.language.isDefault && this.language.primary) {
      return `${this.bracketLabelTranslations.default}, ${this.bracketLabelTranslations.primary}`;
    }
    if (this.language.isDefault) {
      return this.bracketLabelTranslations.default;
    }
    if (this.language.primary) {
      return this.bracketLabelTranslations.primary;
    }
    return '';
  }
}
